import {
	Badge,
	Box,
	Container,
	Heading,
	Icons,
	Inline,
	Spinner,
	Stack,
	TabContent,
	Tabs,
	TabsList,
	Text,
} from "@sembark-travel/ui-base"
import { Breadcrumbs, ButtonLink, NavLink } from "@sembark-travel/ui-router"
import { Helmet } from "react-helmet-async"
import { ReactNode, useEffect, useState } from "react"
import { useXHR } from "@sembark-travel/xhr"
import { generatePath, useNavigate } from "../router-utils"
import {
	DeleteAutomationButton,
	ToggleAutomationButton,
} from "./DefinitionDialog"
import { TAutomation, XHR } from "./store"

function formatStartType(startType: TAutomation["start_type"]) {
	return startType === "schedule" ? "Scheduled" : "Event"
}

export function AutomationItem({
	automationId,
	children,
}: {
	automationId: string
	children?: (args: { automation: TAutomation }) => ReactNode
}) {
	const xhr = useXHR()
	const navigate = useNavigate()
	const [automation, setAutomation] = useState<TAutomation | null>(null)
	const [loading, setLoading] = useState(true)

	useEffect(() => {
		setLoading(true)
		XHR(xhr)
			.show(automationId)
			.then((resp) => setAutomation(resp.data))
			.finally(() => setLoading(false))
	}, [automationId, xhr])

	const definitionIndexPath = generatePath("/org/automations/definitions")
	const breadcrumbItems: Array<[string, string]> = [
		[definitionIndexPath, "Automations"],
		[definitionIndexPath, "Definitions"],
		[
			generatePath("/automations/:automationId", { automationId }),
			automation?.source?.name || automationId,
		],
	]

	return (
		<>
			<Helmet>
				<title>{automation?.source?.name || "Automation"}</title>
			</Helmet>
			<Breadcrumbs
				title={automation?.source?.name || "Automation"}
				items={breadcrumbItems}
			/>
			{loading ? (
				<Spinner padding="8" alignCenter />
			) : !automation ? (
				<Container paddingY="8" bgColor="default">
					<Stack gap="3">
						<Heading as="h3">Automation not found</Heading>
						<Box>
							<ButtonLink to={definitionIndexPath} level="secondary">
								Back to Definitions
							</ButtonLink>
						</Box>
					</Stack>
				</Container>
			) : (
				<Container paddingY="8" bgColor="default">
					<Stack gap="6">
						<Stack gap="4">
							<Inline
								justifyContent="between"
								flexWrap="wrap"
								gap="3"
								collapseBelow="sm"
							>
								<Stack gap="1" flex="1">
									<Heading as="h2">
										{automation.template?.name ||
											automation.source?.name ||
											automation.source_key}
									</Heading>
									<Text color="muted">
										{automation.template?.description ||
											automation.source?.description ||
											"Automation definition"}
									</Text>
								</Stack>
								<Inline gap="2" flexWrap="wrap" justifyContent="end">
									{automation.template_key ? (
										<ButtonLink
											to={generatePath("/automations/:automationId/edit", {
												automationId: String(automation.id),
											})}
											level="secondary"
											size="sm"
										>
											<Icons.Pencil /> Edit
										</ButtonLink>
									) : null}
									<ToggleAutomationButton
										automation={automation}
										onSuccess={(updated) => setAutomation(updated)}
									/>
									<DeleteAutomationButton
										automation={automation}
										onSuccess={() =>
											navigate("/org/automations/definitions", {
												replace: true,
											})
										}
									/>
								</Inline>
							</Inline>
							<Inline gap="4" flexWrap="wrap">
								<Badge
									success={automation.is_active}
									warning={!automation.is_active}
								>
									{automation.is_active ? "Active" : "Inactive"}
								</Badge>
								<Text>{formatStartType(automation.start_type)}</Text>
								<Text>
									{automation.steps_count ?? automation.steps?.length ?? 0} step
									{(automation.steps_count ?? automation.steps?.length ?? 0) !==
									1
										? "s"
										: ""}
								</Text>
								<Text>
									{automation.total_runs_count} run
									{automation.total_runs_count !== 1 ? "s" : ""}
								</Text>
							</Inline>
						</Stack>
						<Tabs borderWidth="1" rounded="md">
							<TabsList>
								<NavLink
									to={generatePath("/automations/:automationId/runs", {
										automationId: String(automation.id),
									})}
								>
									Runs
								</NavLink>
								<NavLink
									to={generatePath("/automations/:automationId/schedules", {
										automationId: String(automation.id),
									})}
								>
									Schedules
								</NavLink>
								<NavLink
									to={generatePath("/automations/:automationId/details", {
										automationId: String(automation.id),
									})}
								>
									Details
								</NavLink>
							</TabsList>
							<TabContent>{children?.({ automation })}</TabContent>
						</Tabs>
					</Stack>
				</Container>
			)}
		</>
	)
}
